import { alpha, Box, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import UploadFile, { FormControlUploadFileProps } from './UploadFile';
import { FormElementControlPropsType } from 'components/common/form/layout/control';
import useBundleTranslation from 'i18n';

export interface FormControlUploadImageProps extends FormControlUploadFileProps {
    previewImageContainer?: {
        uploadPreviewType?: 'text' | 'field';
        noteText?: string;
        width?: string;
        height?: string;
        maxImageHeight?: string;
        maxImageWidth?: string;
    };
}

export default function UploadImage({ controlProps: props }: FormElementControlPropsType<FormControlUploadImageProps>) {
    const { t } = useBundleTranslation(['components/common/upload-control/upload_image']);
    const {
        hidePreview = false,
        previewType = 'image',
        accept = 'image/*',
        sx,
        previewsList = [],
        onChange = () => {},
        previewsPlaceholder,
        previewImageContainer = {},
    } = props;

    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [files, setFiles] = useState<any>();

    const buildPreview = () => {
        if (files?.length >= 0) {
            const previewUrls = [];
            for (let i = 0; i < files.length; i++) {
                previewUrls.push(URL.createObjectURL(files[i]));
            }
            if (previewsPlaceholder && !previewUrls.length) {
                setPreviewImages([previewsPlaceholder]);
            } else {
                setPreviewImages(previewUrls);
            }

            return false;
        } else {
            let defaultValue = props.form.hookFormGetValues(props.name);
            if (
                !defaultValue ||
                !Array.isArray(defaultValue) ||
                (Array.isArray(defaultValue) && defaultValue.length === 0)
            ) {
                defaultValue = previewsList;
            }
            if (defaultValue.length > 0) {
                setPreviewImages(defaultValue);
            } else {
                if (previewsPlaceholder) setPreviewImages([previewsPlaceholder]);
            }
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFiles(event?.target?.files ?? []);
        onChange(event);
    };

    useEffect(() => {
        if (!hidePreview) buildPreview();
    }, [hidePreview, files]);

    const elementProps = { ...props };
    elementProps.onChange = handleChange;
    elementProps.sx = {};
    elementProps.previewType = previewImageContainer?.uploadPreviewType ?? elementProps.previewType;

    return (
        <Stack
            sx={sx}
            direction={previewType === 'image-container' ? 'column' : 'row'}
            className={'upload-image-holder'}
        >
            {!hidePreview && previewType === 'image-container' && previewImages.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                    <Stack
                        alignItems={'center'}
                        justifyContent={'center'}
                        sx={{
                            border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                            borderRadius: 1,
                            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.02),
                            height: previewImageContainer?.height ?? undefined,
                            width: previewImageContainer?.width ?? undefined,
                            p: 1,
                        }}
                    >
                        <Box
                            component={'img'}
                            src={previewImages[0]}
                            alt=""
                            className="upload-image-preview"
                            key={`0_${files?.[0]?.name ?? ''}`}
                            sx={{
                                maxWidth: previewImageContainer?.maxImageWidth ?? '100%',
                                maxHeight: previewImageContainer?.maxImageHeight ?? '100%',
                            }}
                        />
                    </Stack>
                    {previewImageContainer?.noteText && (
                        <Box sx={{ fontStyle: 'italic', color: (theme) => alpha(theme.palette.text.primary, 0.8) }}>
                            {t(previewImageContainer.noteText)}
                        </Box>
                    )}
                </Stack>
            )}
            {!hidePreview && previewType === 'image' && previewImages.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mr: '8px' }} className={'preview-holder'}>
                    {previewImages.map((imageSrc, index) => {
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    img: {
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                    },
                                }}
                                className="upload-image-preview-holder"
                            >
                                <img
                                    src={imageSrc}
                                    alt=""
                                    className="upload-image-preview"
                                    key={`${index}_${files?.[index]?.name ?? ''}`}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <UploadFile controlProps={elementProps} />
        </Stack>
    );
}
